// The mentors' advice libraries — at least 8 lines per mentor, keyed by the
// trigger they respond to. selectAdvice picks the most pressing active trigger a
// mentor covers and returns one line (varied by the week so meetings feel fresh).
// A mentor returns null — and stays silent — if none of their triggers fired.

import type { MentorId, MentorTrigger } from '@biz-kid/engine';

type Library = Partial<Record<MentorTrigger, string[]>>;

// Most-pressing first. selectAdvice walks this order and speaks to the first
// active trigger the mentor has a line for.
const PRIORITY: MentorTrigger[] = ['inflation', 'lossWeek', 'freedomLow', 'lowSavings', 'cashIdle', 'lowPersistence', 'dreamAchieved'];

export const ADVICE: Record<MentorId, Library> = {
  richDad: {
    cashIdle: ['Cash sitting still is lazy money. Buy something that pays you — an asset, not a toy.', "Don't let money sleep. Put it to work buying assets."],
    lowSavings: ['The poor spend, then save what’s left. Flip it — build assets first, spend what remains.'],
    freedomLow: ['Buy assets until their income covers your life. That is the entire game.'],
    lossWeek: ['A loss is tuition. What did this week teach you about your numbers?'],
    lowPersistence: ['Most quit one asset before the breakthrough. Keep stacking.'],
    dreamAchieved: ['See? Assets bought you that. Now go buy the next one.', 'That’s your money working for you. Do it again.'],
  },
  investor: {
    // Buffett-snowball warning — verbatim, fires only after 2+ idle years.
    inflation: ['That cash in your pocket is shrinking every day. Inflation is a thief. Put it to work.'],
    cashIdle: ['Idle cash loses to inflation. Even small amounts, invested, compound into mountains.'],
    lowSavings: ['You can’t compound what you don’t keep. Save first — make it automatic.'],
    freedomLow: ['Dividends are income you never work for. Build a steady stream of them.', 'Reinvest every dividend — that’s where the magic hides.'],
    lossWeek: ['Down weeks are when patient investors buy. Don’t panic — zoom out.'],
    lowPersistence: ['Compounding rewards the patient. Time in the market beats timing it.'],
    dreamAchieved: ['Compounding made that possible. Let it keep working.', 'Slow, then sudden. Stay in the game.'],
  },
  propertyQueen: {
    cashIdle: ['Money in the bank pays you nothing. A small property pays you every single month.'],
    lowSavings: ['Save your deposit relentlessly. The down payment is the door.'],
    freedomLow: ['One rental at a time — each is a brick in the wall of your freedom.', 'Don’t wait to buy property. Buy property, then wait.'],
    lossWeek: ['Even in a tough month, the rent still comes in. That’s why I love property.'],
    lowPersistence: ['Property rewards the steady. Buy, hold, repeat.'],
    dreamAchieved: ['Bricks and patience built that. Now stack the next one.'],
  },
  mindsetCoach: {
    lowPersistence: ['Persistence is to character what carbon is to steel. Decide, then refuse to quit.', 'A quitter never wins. Take one more step today.'],
    dreamAchieved: ['You held the picture, you did the work, and it became real. That is the whole secret.'],
    freedomLow: ['Set a definite goal and a definite plan. Vague wishes get vague results.'],
    lossWeek: ['Every failure carries the seed of an equal or greater success. Go find it.'],
    lowSavings: ['Self-discipline starts small — like saving a little, every time, no excuses.'],
    cashIdle: ['Desire plus a plan turns idle resources into real results. Use what you have.'],
  },
  techFounder: {
    cashIdle: ['Reinvest it into something that scales — built once, it works while you sleep.'],
    lossWeek: ['Every failed week is data. Iterate, ship again — smaller and faster.', 'Fail fast, learn faster, ship the fix.'],
    lowPersistence: ['Founders survive the boring middle. Keep shipping.'],
    freedomLow: ['Build it once, sell it a thousand times. That’s leverage.'],
    lowSavings: ['Keep a runway. Cash is the time you need to figure it out.'],
    dreamAchieved: ['Shipped it! Now — what’s version two?'],
  },
  socialStrategist: {
    cashIdle: ['Invest in your audience — followers today are customers tomorrow.'],
    dreamAchieved: ['Tell that story! Your wins are your best content.', 'Share the journey — people root for builders.'],
    lowPersistence: ['Posting once is noise. Showing up every day is a brand.'],
    freedomLow: ['An audience that trusts you is an asset that pays for years.'],
    lossWeek: ['A flop just tells you what your audience didn’t want. Adjust and post again.'],
    lowSavings: ['Don’t spend your first wins flexing — reinvest in reach.'],
  },
  banker: {
    lossWeek: ['Watch your debts in a down week — never borrow to cover a loss without a plan.'],
    lowSavings: ['An emergency fund is your seatbelt. Three months of costs, untouched.', 'Pay yourself first — automatically, before anything else.'],
    freedomLow: ['Good debt buys assets that pay the loan. Bad debt buys toys. Know the difference.'],
    cashIdle: ['Keep a buffer for safety — but cash past that is lazy. Put the rest to work.'],
    lowPersistence: ['Pay every bill on time. Boring habits build a powerful credit score.'],
    dreamAchieved: ['Well funded. Keep your leverage sensible and you’ll keep winning.'],
  },
  philanthropist: {
    dreamAchieved: ['Beautiful. Now — who can you lift up with what you’ve built?', 'Significance is what you give, not just what you gather.'],
    freedomLow: ['Build your freedom first, so your giving can be generous and lasting.'],
    cashIdle: ['Money is a tool — some to grow, some to give. Both make you richer.'],
    lossWeek: ['Hard weeks build empathy. Remember this feeling when you’re able to help others.'],
    lowSavings: ['Give a little even while you build. Generosity is a habit, not an amount.'],
    lowPersistence: ['Legacy is built over decades. Keep going — for those who come after.'],
  },
};

export type MentorAdvice = { trigger: MentorTrigger; text: string };

/** Pick a mentor's line for the week, or null if none of their triggers fired. */
export function selectAdvice(id: MentorId, active: MentorTrigger[], weekSeed: number): MentorAdvice | null {
  const lib = ADVICE[id];
  for (const trig of PRIORITY) {
    if (!active.includes(trig)) continue;
    const lines = lib[trig];
    if (lines && lines.length) {
      return { trigger: trig, text: lines[weekSeed % lines.length]! };
    }
  }
  return null;
}

export const TRIGGER_LABEL: Record<MentorTrigger, string> = {
  lowSavings: 'on your savings',
  lowPersistence: 'on staying the course',
  freedomLow: 'on the road to freedom',
  dreamAchieved: 'on your win',
  lossWeek: 'on last week’s loss',
  cashIdle: 'on your idle cash',
  inflation: 'on inflation',
};
